// Pictures from outside the app (D-70): pasted (a site's "Copy image", a screenshot) or dropped (a file, a picture
// dragged from another site). Everything happens in the browser; FlexPDF never downloads a picture it is only given a
// link to.

/**
 * `blob` as a picture file the engine takes: a PNG or JPEG as it is (checked that the browser can draw it); other
 * kinds (WebP, GIF, AVIF, BMP…) drawn into a PNG here.
 */
export async function pictureFile(blob: Blob, name = 'picture'): Promise<File> {
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(blob);
  } catch {
    throw new Error('the browser cannot read it as a picture');
  }
  const base = (blob instanceof File && blob.name ? blob.name : name).replace(/\.[^.]*$/, '');
  if (blob.type === 'image/png' || blob.type === 'image/jpeg') {
    bmp.close();
    return blob instanceof File && blob.name ? blob : new File([blob], `${base}.${blob.type === 'image/png' ? 'png' : 'jpg'}`, { type: blob.type });
  }
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  canvas.getContext('2d')!.drawImage(bmp, 0, 0);
  bmp.close();
  const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  canvas.width = 0;
  if (!png) throw new Error('it could not be turned into a PNG');
  return new File([png], `${base}.png`, { type: 'image/png' });
}

/**
 * The picture a drop carries: an image file; 'link' when it only points at one (a picture dragged from a site that
 * gives no file); undefined otherwise.
 */
export function droppedPicture(dt: DataTransfer): File | 'link' | undefined {
  const file = [...dt.files].find((f) => f.type.startsWith('image/'));
  if (file) return file;
  const offered = `${dt.getData('text/html')} ${dt.getData('text/uri-list')}`;
  return /<img\b|\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|#|\s|$)/i.test(offered) ? 'link' : undefined;
}

/** What FlexPDF itself last put on the system clipboard (AC-CLIP-1): its text, and its picture's size in pixels. */
let written: { text?: string; size?: [number, number] } = {};

export function rememberWritten(text: string | undefined, picture: Blob | undefined): void {
  written = { text };
  if (picture)
    void createImageBitmap(picture).then((b) => {
      written.size = [b.width, b.height];
      b.close();
    });
}

/**
 * Whether a paste holds what FlexPDF copied itself (then its own copy is pasted, with the PDF's fonts and vectors):
 * the same text; or, for a picture alone, a picture of the same size (the browser re-encodes it on the way, so the
 * bytes differ).
 */
export async function isOwnPaste(text: string, picture: File | undefined): Promise<boolean> {
  const plain = (s: string) => s.replace(/\r\n/g, '\n');
  if (written.text !== undefined && text && plain(text) === plain(written.text)) return true;
  if (!picture || !written.size || written.text) return false;
  try {
    const b = await createImageBitmap(picture);
    const same = b.width === written.size[0] && b.height === written.size[1];
    b.close();
    return same;
  } catch {
    return false;
  }
}
